import React from "react";
import PropTypes from "prop-types";
import styles from "./StatsPerCategorySection.module.css";
import { CategoryToggle } from "../CategoryToggle";
import { TrendingBarChart } from "../TrendingBarChart";
import StatsPieChart from "../CategoryBreakdownPieChart";

const CATEGORIES = ["Audios", "Videos", "Playlists", "Radio"];
const TIMEFRAME_OPTIONS = ["This Week", "This Month", "This Year"];

const PLACEHOLDER_BAR_DATA = {
  Audios: {
    "This Week": [
      { label: "Sermons", value: 43000 },
      { label: "Worship", value: 38000 },
      { label: "Teaching", value: 50000 },
      { label: "Podcasts", value: 32000 },
      { label: "Stories", value: 27000 },
    ],
    "This Month": [
      { label: "Sermons", value: 180000 },
      { label: "Worship", value: 162000 },
      { label: "Teaching", value: 210000 },
      { label: "Podcasts", value: 134000 },
      { label: "Stories", value: 119000 },
    ],
    "This Year": [
      { label: "Sermons", value: 1920000 },
      { label: "Worship", value: 1740000 },
      { label: "Teaching", value: 2200000 },
      { label: "Podcasts", value: 1450000 },
      { label: "Stories", value: 1230000 },
    ],
  },
  Videos: {
    "This Week": [
      { label: "Sermons", value: 28000 },
      { label: "Worship", value: 22000 },
      { label: "Teaching", value: 35000 },
      { label: "Events", value: 19000 },
      { label: "Shorts", value: 16000 },
    ],
    "This Month": [
      { label: "Sermons", value: 115000 },
      { label: "Worship", value: 94000 },
      { label: "Teaching", value: 142000 },
      { label: "Events", value: 78000 },
      { label: "Shorts", value: 67000 },
    ],
    "This Year": [
      { label: "Sermons", value: 1240000 },
      { label: "Worship", value: 1010000 },
      { label: "Teaching", value: 1530000 },
      { label: "Events", value: 840000 },
      { label: "Shorts", value: 720000 },
    ],
  },
  Playlists: {
    "This Week": [
      { label: "Morning", value: 15000 },
      { label: "Evening", value: 12000 },
      { label: "Workout", value: 18000 },
      { label: "Focus", value: 9000 },
      { label: "Relax", value: 7000 },
    ],
    "This Month": [
      { label: "Morning", value: 62000 },
      { label: "Evening", value: 49000 },
      { label: "Workout", value: 75000 },
      { label: "Focus", value: 37000 },
      { label: "Relax", value: 29000 },
    ],
    "This Year": [
      { label: "Morning", value: 660000 },
      { label: "Evening", value: 530000 },
      { label: "Workout", value: 810000 },
      { label: "Focus", value: 400000 },
      { label: "Relax", value: 310000 },
    ],
  },
  Radio: {
    "This Week": [
      { label: "Station 1", value: 9100 },
      { label: "Station 2", value: 8200 },
      { label: "Station 3", value: 7600 },
      { label: "Station 4", value: 6400 },
      { label: "Station 5", value: 5100 },
    ],
    "This Month": [
      { label: "Station 1", value: 38000 },
      { label: "Station 2", value: 34000 },
      { label: "Station 3", value: 31000 },
      { label: "Station 4", value: 26000 },
      { label: "Station 5", value: 21000 },
    ],
    "This Year": [
      { label: "Station 1", value: 410000 },
      { label: "Station 2", value: 370000 },
      { label: "Station 3", value: 335000 },
      { label: "Station 4", value: 280000 },
      { label: "Station 5", value: 225000 },
    ],
  },
};

const PLACEHOLDER_PIE1_DATA = {
  Audios: [
    { label: "Completed", value: 4200 },
    { label: "In Progress", value: 2800 },
    { label: "Queued", value: 1500 },
    { label: "Skipped", value: 900 },
  ],
  Videos: [
    { label: "Completed", value: 3100 },
    { label: "In Progress", value: 2100 },
    { label: "Queued", value: 900 },
    { label: "Skipped", value: 600 },
  ],
  Playlists: [
    { label: "Created", value: 1800 },
    { label: "Active", value: 1200 },
    { label: "Archived", value: 400 },
    { label: "Shared", value: 300 },
  ],
  Radio: [
    { label: "Listened", value: 2200 },
    { label: "Saved", value: 800 },
    { label: "Skipped", value: 600 },
    { label: "Queued", value: 200 },
  ],
};

const PLACEHOLDER_PIE2_DATA = {
  Audios: [
    { label: "Mobile", value: 5300 },
    { label: "Desktop", value: 3200 },
    { label: "Tablet", value: 900 },
    { label: "Other", value: 400 },
  ],
  Videos: [
    { label: "Mobile", value: 3900 },
    { label: "Desktop", value: 2800 },
    { label: "Tablet", value: 700 },
    { label: "Other", value: 200 },
  ],
  Playlists: [
    { label: "Mobile", value: 2100 },
    { label: "Desktop", value: 1800 },
    { label: "Tablet", value: 400 },
    { label: "Other", value: 100 },
  ],
  Radio: [
    { label: "Mobile", value: 2500 },
    { label: "Desktop", value: 900 },
    { label: "Tablet", value: 300 },
    { label: "Other", value: 100 },
  ],
};

export function StatsPerCategorySection({
  activeCategory,
  data,
  onCategoryChange,
}) {
  const [timeframe, setTimeframe] = React.useState("This Week");

  const categoryData = data?.[activeCategory];

  const barData =
    categoryData?.barData?.[timeframe] ??
    PLACEHOLDER_BAR_DATA[activeCategory]?.[timeframe] ??
    PLACEHOLDER_BAR_DATA.Audios["This Week"];

  const pie1Data =
    categoryData?.pie1Data ??
    PLACEHOLDER_PIE1_DATA[activeCategory] ??
    PLACEHOLDER_PIE1_DATA.Audios;

  const pie2Data =
    categoryData?.pie2Data ??
    PLACEHOLDER_PIE2_DATA[activeCategory] ??
    PLACEHOLDER_PIE2_DATA.Audios;

  return (
    <div className={styles.section}>
      <div className={styles.sectionHeader}>
        <span className={styles.headerLine} />
        <h2 className={styles.headerTitle}>Stats Per Category</h2>
        <span className={styles.headerLine} />
      </div>

      <CategoryToggle
        options={CATEGORIES}
        active={activeCategory}
        onChange={onCategoryChange}
      />

      <div className={styles.chartsRow}>
        <TrendingBarChart
          title="Activity"
          data={barData}
          timeframe={timeframe}
          summaryValue=""
          onTimeframeChange={setTimeframe}
        />
        <StatsPieChart data={pie1Data} />
        <StatsPieChart data={pie2Data} />
      </div>
    </div>
  );
}

StatsPerCategorySection.propTypes = {
  activeCategory: PropTypes.oneOf(CATEGORIES).isRequired,
  data: PropTypes.shape({
    barData: PropTypes.shape(
      TIMEFRAME_OPTIONS.reduce((acc, tf) => {
        acc[tf] = PropTypes.arrayOf(
          PropTypes.shape({
            label: PropTypes.string.isRequired,
            value: PropTypes.number.isRequired,
          }),
        );
        return acc;
      }, {}),
    ),
    pie1Data: PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string.isRequired,
        value: PropTypes.number.isRequired,
      }),
    ),
    pie2Data: PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string.isRequired,
        value: PropTypes.number.isRequired,
      }),
    ),
  }),
  onCategoryChange: PropTypes.func.isRequired,
};
